import React, { Suspense, lazy } from "react";
import { Switch, Route } from "react-router-dom";
import Layout from "./Layout";
import store from "./redux/store";
import { Provider, useDispatch, useSelector } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { persistStore } from "redux-persist";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { ThemeProvider as LegacyThemeProvider } from "@material-ui/core/styles";
import MiniDrawer from "./components/MiniDrawer";
import Loading from "./common/Loading";
import DashboardView from "./views/DashboardView";
import { createAppTheme, createLegacyAppTheme } from "./theme";
import { setTheme } from "./redux/session";

const LoginView = lazy(() => import("./views/LoginView"));
const NoMatch = lazy(() => import("./views/NoMatch"));

const VideoCallView = lazy(() => import("./views/VideoCallView"));
const ConfigView = lazy(() => import("./views/ConfigView"));
const RegistersView = lazy(() => import("./views/RegistersView"));

const HumanResourses = lazy(() => import("./views/HumanResourses"));
const Companies = lazy(() => import("./views/Companies"));
const LinksDevices = lazy(() => import("./views/LinksDevices"));
const Locations = lazy(() => import("./views/Locations"));
const IndustrialAssets = lazy(() => import("./views/IndustrialAssets"));
const Devices = lazy(() => import("./views/Devices"));
const Sections = lazy(() => import("./views/Sections"));
const Catalogs = lazy(() => import("./views/Catalogs"));
const BrokerCatalogs = lazy(() => import("./views/BrokerCatalogs"));
const Clients = lazy(() => import("./views/Clients"));
const WorkGroups = lazy(() => import("./views/WorkGroups"));
const Calendar = lazy(() => import("./views/Calendar"));
const ViewerView = lazy(() => import("./views/ViewerView"));
const HomeView = lazy(() => import("./views/HomeView"));
const ClientProductsView = lazy(() => import("./views/ClientProductsView"));
const AuthHandoffView = lazy(() => import("./views/AuthHandoffView"));

console.log('[versions][client]', {
  socketioClient: require('socket.io-client/package.json').version,
});
function App() {
  let persistor = persistStore(store);

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <AppContainer />
      </PersistGate>
    </Provider>
  );
}

function AppContainer() {
  const dispatch = useDispatch();
  const themeCustom = useSelector((state) => state.session.theme);
  const appTheme = React.useMemo(() => createAppTheme(themeCustom), [themeCustom]);
  const legacyTheme = React.useMemo(() => createLegacyAppTheme(themeCustom), [themeCustom]);

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem('design-theme');
      const parsed = raw ? JSON.parse(raw) : {};
      if (parsed && typeof parsed === 'object') {
        dispatch(setTheme({
          mode: parsed.mode || 'light',
          rounded: parsed.rounded || 'soft',
          size: parsed.size || 'normal',
          ...parsed,
          colorId: parsed.colorId || 'black',
          glass: parsed.glass ?? true,
        }));
      } else {
        dispatch(setTheme({
          mode: 'light',
          colorId: 'black',
          rounded: 'soft',
          size: 'normal',
          glass: true,
        }));
      }
    } catch (e) { }
  }, [dispatch]);

  return (
    <ThemeProvider theme={appTheme}>
      <LegacyThemeProvider theme={legacyTheme}>
        <CssBaseline />
        <MiniDrawer>
          <Switch>
            {[
              { path: "/", exact: true, component: <DashboardView /> },
              { path: "/mis-productos", component: <ClientProductsView /> },
              { path: "/home", component: <HomeView /> },
              { path: "/work-groups", component: <WorkGroups /> },
              { path: "/calendar", component: <Calendar /> },
              { path: "/users", component: <HumanResourses /> },
              { path: "/clients", component: <Clients /> },
              { path: "/companies", component: <Companies /> },
              { path: "/links", component: <LinksDevices /> },
              { path: "/locations", component: <Locations /> },
              { path: "/sections", component: <Sections /> },
              { path: "/industrial-assets", component: <IndustrialAssets /> },
              { path: "/devices", component: <Devices /> },
              { path: "/login", component: <LoginView /> },
              { path: "/auth/handoff", component: <AuthHandoffView /> },
              { path: "/video-call", component: <VideoCallView /> },
              { path: "/viewer", component: <ViewerView /> },
              { path: "/config", component: <ConfigView /> },
              { path: "/catalogs", component: <Catalogs /> },
              { path: "/broker-catalogs", component: <BrokerCatalogs /> },
              { path: "/registers", component: <RegistersView /> },
              { path: "*", component: <NoMatch /> },
            ].map(({ path, exact = false, component }, index) => (
              <Route key={index} path={path} exact={exact}>
                <Suspense
                  fallback={
                    <div style={{}}>
                      <Loading />
                    </div>
                  }
                >
                  {component}
                </Suspense>
              </Route>
            ))}
          </Switch>
        </MiniDrawer>
      </LegacyThemeProvider>
    </ThemeProvider>
  );
}

export default App;
